import type { UserProfile, UserProfileRead } from "epsm-api-types/models";
import type React from "react";
import { lazy, Suspense } from "react";
import { VisuallyHidden } from "react-aria";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const StripeAddressForm = lazy(() =>
	import(
		/* webpackChunkName: "stripe-address-form" */
		"@/shared/components/StripeAddressForm"
	).then((m) => ({ default: m.StripeAddressForm })),
);

import { Spinner } from "@/components/Spinner";
import { formClassNames } from "@/shared/constants/customStyleSets";
import type {
	BillingAddress,
	FormType,
	SubscriptionPeriod,
	SubscriptionPlan,
} from "../../../types";
import { BillingPreview } from "../BillingPreview";
import { SuccessUpdateForm } from "../SuccessUpdateForm";

type Props = {
	openModal: FormType | null;
	stripeCheckout: (data: BillingAddress) => Promise<void>;
	onSuccessUpdate: (shouldRedirectToDashboard?: boolean) => void;
	updateSubscription: (
		plan: SubscriptionPlan | null,
		period: SubscriptionPeriod,
	) => Promise<Error | undefined>;
	setOpenModal: (modal: FormType | null) => void;
	period: SubscriptionPeriod;
	selectedPlan: SubscriptionPlan | null;
	userData: Partial<UserProfile & UserProfileRead> | null;
};

export const RenderDialogContent: React.FC<Props> = (props) => {
	const {
		openModal,
		stripeCheckout,
		setOpenModal,
		onSuccessUpdate,
		updateSubscription,
		period,
		selectedPlan,
		userData,
	} = props;

	const mapForm: Record<FormType, React.JSX.Element> = {
		address: (
			<Suspense fallback={<Spinner />} aria-label="Loading address form">
				<StripeAddressForm stripeCheckout={stripeCheckout} />
			</Suspense>
		),
		success: <SuccessUpdateForm onSuccess={onSuccessUpdate} />,
		preview: (
			<BillingPreview
				onConfirm={updateSubscription}
				newPeriod={period}
				selectedPlan={selectedPlan}
				userData={userData}
			/>
		),
	};

	if (!openModal) return null;

	return (
		<Dialog
			open={!!openModal}
			onOpenChange={() => {
				setOpenModal(null);
			}}
		>
			<DialogContent className={cn(formClassNames, "max-w-[900px]")}>
				<VisuallyHidden>
					<DialogTitle>Pricing Table</DialogTitle>
				</VisuallyHidden>

				{mapForm[openModal]}
			</DialogContent>
		</Dialog>
	);
};
